import React, { useState, useEffect, useCallback } from 'react';
import {
  BellIcon, PaperAirplaneIcon, UsersIcon, CheckCircleIcon, BeakerIcon,
} from '@heroicons/react/24/outline';
import { adminAPI } from '../services/api';
import styles from './NotificationsPage.module.css';

/**
 * What Expo's error codes mean, and whose problem each one is.
 *
 * The codes alone are no help to someone deciding what to do next:
 * InvalidCredentials needs a key uploading, DeviceNotRegistered needs
 * nothing at all.
 */
const FAILURE_REASONS = {
  DeviceNotRegistered: 'app uninstalled — these devices have been retired',
  InvalidCredentials:  'push key missing or wrong in Expo — needs fixing before this can work',
  MessageTooBig:       'the message is too long',
  MessageRateExceeded: 'sent too fast — Expo throttled it',
  MismatchSenderId:    'the FCM key does not match this app',
  NetworkError:        'could not reach Expo',
  RequestRejected:     'Expo rejected the whole batch',
  Unknown:             'Expo gave no reason',
};

// The fallback list, used only until the server answers.
//
// The real list comes from the API, because it and the queries behind it kept
// drifting apart: this dropdown offered "Expiring in 30 Days" while the query
// had no lower bound and compared a DATE against NOW(), so it reached nobody
// at all. One definition, one source, and a live count beside each so a send
// that would go nowhere is visible before the button rather than after.
const AUDIENCES = [
  { value: 'all',              label: 'All Users',              desc: 'Everyone with the app installed' },
  { value: 'vip_active',       label: 'Active VIP Members',     desc: 'Currently active VIP members' },
  { value: 'registered_no_vip',label: 'Registered — No VIP',    desc: 'Users who never purchased VIP' },
  { value: 'expiring_30',      label: 'Expiring in 30 Days',    desc: 'VIP memberships expiring soon' },
  { value: 'expiring_60',      label: 'Expiring in 31–60 Days', desc: 'Proactive renewal targets' },
  { value: 'churned',          label: 'Churned — Expired VIP',  desc: 'Win-back campaign audience' },
  // By platform, for announcing a release. The two stores approve on their
  // own schedules, so telling Android owners about an iOS update sends them
  // to a store with nothing new — which reads as the app being broken.
  { value: 'ios_outdated',     label: 'iPhone — not yet updated', desc: 'Only the people still on an older version' },
  { value: 'android_outdated', label: 'Android — not yet updated', desc: 'Only the people still on an older version' },
  // For picking up after a failed send. Resending to the whole platform would
  // notify everyone who already got it a second time, which is how an
  // announcement turns into a nuisance.
  { value: 'ios_missed',       label: 'iPhone — did not receive the last one', desc: 'Devices never reached, or whose last send failed' },
  { value: 'android_missed',   label: 'Android — did not receive the last one', desc: 'Devices never reached, or whose last send failed' },
  { value: 'ios',              label: 'iPhone users only',      desc: 'Every iPhone, updated or not' },
  { value: 'android',          label: 'Android users only',     desc: 'Every Android device, updated or not' },
];

// Written to the lock screen's limits: iOS cuts the title near 40 characters
// and the body near 110, so anything longer is read as a sentence that stops
// halfway. No "tap here" — the whole notification is already tappable — and
// no emoji in titles, which eat the budget and date badly.
//
// `audience` sets the recipient list to match when a template is chosen,
// because the commonest mistake is a renewal reminder sent to everybody.
const TEMPLATES = [
  { label: 'App update — iPhone',  audience: 'ios_outdated',
    title: 'A new version is ready',
    body:  'Update The Toy Store VIP for a faster sign-in and a smoother card.' },
  { label: 'App update — Android', audience: 'android_outdated',
    title: 'A new version is ready',
    body:  'Update The Toy Store VIP for a faster sign-in and a smoother card.' },
  { label: 'Expired — win back',   audience: 'churned',
    title: 'Your VIP card has expired',
    body:  'Renew in the app and get back to 25% off every visit.' },
  { label: 'Expiring in 30 days',  audience: 'expiring_30',
    title: 'Your VIP expires soon',
    body:  'Renew now and keep your 25% off without a break.' },
  { label: 'Never joined',         audience: 'registered_no_vip',
    title: '25% off every toy, all year',
    body:  'Join VIP in the app or at any till — Ramlet El Bayda and Downtown.' },
  { label: 'New arrivals',         audience: 'vip_active',
    title: 'New arrivals, VIP first',
    body:  'See what just landed before anyone else. Open the app to browse.' },
  { label: 'Eid greeting',         audience: 'all',
    title: 'Eid Mubarak from The Toy Store',
    body:  'Wishing you and your family a joyful Eid. Your VIP discount is waiting in store.' },
  { label: 'Birthday',             audience: 'all',
    title: 'Happy Birthday!',
    body:  'The Toy Store wishes you a magical birthday. A surprise is waiting in store.' },
];

export default function NotificationsPage() {
  const [campaigns, setCampaigns]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [form, setForm]             = useState({ title: '', body: '', audience: 'all' });

  // The audiences, from the server, with how many devices each reaches.
  //
  // Falls back to the hardcoded list if the call fails, so a network blip
  // leaves a usable dropdown rather than an empty one -- but the counts are
  // the point, and they only come from the server.
  const [audiences, setAudiences] = useState(
    AUDIENCES.map(a => ({ key: a.value, label: a.label, hint: a.desc, group: 'Audiences', devices: null }))
  );
  useEffect(() => {
    let gone = false;
    adminAPI.pushAudiences()
      .then(r => { if (!gone && r.audiences?.length) setAudiences(r.audiences); })
      .catch(() => {});
    return () => { gone = true; };
  }, []);

  const audienceGroups = Object.entries(
    audiences.reduce((acc, a) => { (acc[a.group] = acc[a.group] || []).push(a); return acc; }, {})
  );
  const selectedAudience = audiences.find(a => a.key === form.audience) || null;
  const [sending, setSending]       = useState(false);
  const [testing, setTesting]       = useState(false);
  const [testMobile, setTestMobile] = useState('');
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState('');

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAPI.getPushCampaigns();
      setCampaigns(r.campaigns || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  // The audience comes with the template. Choosing "Your VIP expires soon"
  // and leaving the recipients on All Users is the easiest mistake to make
  // here, and the one nobody notices until it has been sent.
  const handleTemplate = (t) =>
    setForm(f => ({ ...f, title: t.title, body: t.body, audience: t.audience || f.audience }));

  /**
   * Try it on one device first.
   *
   * A push cannot be recalled, so finding out that a title truncates or a
   * body reads badly on a lock screen is a mistake that otherwise lands on
   * every customer at once.
   */
  const handleTest = async () => {
    if (!form.title || !form.body) return setError('Title and message are required.');
    setTesting(true); setError(''); setResult(null);
    try {
      const r = await adminAPI.testPushNotification({
        title: form.title, body: form.body,
        mobile: testMobile.trim() || undefined,
      });
      setResult({ ...r, isTest: true });
    } catch (e) { setError(e.message); }
    finally { setTesting(false); }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body) return setError('Title and message are required.');
    if (!window.confirm(`Send "${form.title}" to ${AUDIENCES.find(a => a.value === form.audience)?.label}?`)) return;
    setSending(true); setError(''); setResult(null);
    try {
      const r = await adminAPI.sendPushNotification(form);
      setResult(r);
      setForm({ title: '', body: '', audience: 'all' });
      loadCampaigns();
    } catch (e) { setError(e.message); }
    finally { setSending(false); }
  };

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Push Notifications</h1>
          <p className={styles.pageSub}>Send targeted messages to users by segment or campaign.</p>
        </div>
      </div>

      <div className={styles.layout}>
        {/* ── Compose ── */}
        <div className={styles.composeCard}>
          <div className={styles.cardHeader}>
            <BellIcon style={{ width: 18, height: 18 }} />
            <span>Compose Notification</span>
          </div>

          {/* Templates */}
          <div className={styles.templates}>
            <div className={styles.templatesLabel}>Quick Templates</div>
            <div className={styles.templateGrid}>
              {TEMPLATES.map(t => (
                <button key={t.label} className={styles.templateBtn} onClick={() => handleTemplate(t)}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSend} className={styles.form}>
            <div className={styles.formGroup}>
              <label>Audience</label>
              <select className={styles.input} value={form.audience} onChange={e => setForm(f => ({ ...f, audience: e.target.value }))}>
                {audienceGroups.map(([group, list]) => (
                  <optgroup key={group} label={group}>
                    {list.map(a => (
                      <option key={a.key} value={a.key} disabled={a.unavailable}>
                        {a.label}
                        {a.devices !== null && a.devices !== undefined
                          ? ` — ${a.devices} device${a.devices === 1 ? '' : 's'}`
                          : a.unavailable ? ' — unavailable' : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {/* Said plainly under the field rather than only in the option
                  text, because the number is what stops the mistake: an
                  audience reading 0 is a send that goes nowhere. */}
              {selectedAudience && (
                <p style={{ fontSize:12, color: selectedAudience.devices === 0 ? '#B45309' : 'var(--text-muted)', margin:'6px 0 0' }}>
                  {selectedAudience.hint}
                  {selectedAudience.devices === 0 && ' — nobody matches this right now, so nothing would be sent.'}
                </p>
              )}
            </div>

            <div className={styles.formGroup}>
              <label>Title *</label>
              <input
                className={styles.input}
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Notification title..."
                maxLength={65}
              />
              <span className={styles.charCount}>{form.title.length}/65</span>
            </div>

            <div className={styles.formGroup}>
              <label>Message *</label>
              <textarea
                className={styles.textarea}
                value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                placeholder="Write your message here..."
                rows={4}
                maxLength={240}
              />
              <span className={styles.charCount}>{form.body.length}/240</span>
            </div>

            {error && <div className={styles.errorBanner}>{error}</div>}

            {result && (
              <div className={styles.successBanner}>
                <CheckCircleIcon style={{ width: 16, height: 16 }} />
                Sent to {result.sentCount} device{result.sentCount !== 1 ? 's' : ''}
                {result.isTest
                  ? ` — test only, to ${(result.to || []).join(', ') || 'your device'}`
                  : (result.failCount > 0 ? ` (${result.failCount} failed)` : '')}
                {result.failureDetail && Object.keys(result.failureDetail).length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.6 }}>
                    {Object.entries(result.failureDetail).map(([code, n]) => (
                      <div key={code}>
                        <strong>{n}</strong> — {FAILURE_REASONS[code] || code}
                      </div>
                    ))}
                    {result.retired > 0 && (
                      <div style={{ opacity: .75 }}>
                        {result.retired} dead device{result.retired === 1 ? '' : 's'} removed from future sends.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Test first. Above the send button, because that is the order
                the two should happen in. */}
            <div style={{
              display:'flex', gap:8, alignItems:'center', flexWrap:'wrap',
              padding:'12px 0', marginTop:4, borderTop:'1px solid var(--border)',
            }}>
              <input
                value={testMobile}
                onChange={e => setTestMobile(e.target.value)}
                placeholder="Your mobile, e.g. 03999021"
                inputMode="tel"
                style={{ flex:'1 1 180px', minWidth:0, padding:'9px 12px',
                         border:'1px solid var(--border)', borderRadius:8, fontSize:14 }}
              />
              <button type="button" className="btn-ghost" onClick={handleTest} disabled={testing}>
                <BeakerIcon style={{ width:15, height:15 }} />
                {testing ? 'Sending test…' : 'Send test to me'}
              </button>
            </div>

            <button type="submit" className={styles.btnSend} disabled={sending || testing}>
              <PaperAirplaneIcon style={{ width: 16, height: 16 }} />
              {sending ? 'Sending...' : 'Send Notification'}
            </button>
          </form>
        </div>

        {/* ── History ── */}
        <div className={styles.historyCard}>
          <div className={styles.cardHeader}>
            <UsersIcon style={{ width: 18, height: 18 }} />
            <span>Sent Campaigns</span>
          </div>

          {loading ? (
            <div className={styles.skeleton}>
              {[...Array(4)].map((_, i) => <div key={i} className={styles.skeletonRow} />)}
            </div>
          ) : campaigns.length === 0 ? (
            <div className={styles.empty}>No campaigns sent yet.</div>
          ) : (
            <div className={styles.campaignList}>
              {campaigns.map(c => (
                <div key={c.id} className={styles.campaignItem}>
                  <div className={styles.campaignTop}>
                    <span className={styles.campaignTitle}>{c.title}</span>
                    <span className={styles.audienceBadge}>{AUDIENCES.find(a => a.value === c.audience)?.label || c.audience}</span>
                  </div>
                  <p className={styles.campaignBody}>{c.body}</p>
                  <div className={styles.campaignMeta}>
                    {/* "Sent" counts devices, not people: one member with a
                        phone and a tablet is two. The read percentage divides
                        by members, so the two numbers differ and the reason
                        was invisible — a campaign read "20 sent, 0 read (0%)"
                        against nine people. */}
                    <span className={styles.campaignSent}>
                      {c.recipients > 0 ? `${c.recipients} member${c.recipients === 1 ? '' : 's'}` : `${c.sent_count} sent`}
                    </span>
                    {c.sent_count !== c.recipients && c.recipients > 0 && (
                      <span className={styles.campaignDate}>{c.sent_count} devices</span>
                    )}
                    {c.recipients > 0 && (
                      <span className={styles.campaignRead}>
                        {c.read_count} opened the app
                        <span className={styles.campaignReadPct}>
                          {" "}({Math.round((c.read_count / c.recipients) * 100)}%)
                        </span>
                      </span>
                    )}
                    {c.fail_count > 0 && (
                      <span
                        className={styles.campaignFail}
                        title={c.failure_detail
                          ? Object.entries(c.failure_detail)
                              .map(([code, n]) => `${n} — ${FAILURE_REASONS[code] || code}`)
                              .join('\n')
                          : undefined}
                      >
                        {c.fail_count} failed
                        {c.failure_detail && Object.keys(c.failure_detail).length === 1 && (
                          <> · {FAILURE_REASONS[Object.keys(c.failure_detail)[0]]
                                || Object.keys(c.failure_detail)[0]}</>
                        )}
                      </span>
                    )}
                    <span className={styles.campaignDate}>{new Date(c.sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    <span className={styles.campaignBy}>by {c.sent_by_name || 'Admin'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
